import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';

import {
    COLOR_BLUE,
    COLOR_MINT,
    COLOR_OUTLINE,
    COLOR_SLATE_LIGHT,
    COLOR_TEAL
} from '../../../theme';
import {
    NOTIFICATION_MODE_HELP_TEXTS,
    NOTIFICATION_MODE_LABELS,
    WatchlistNotificationMode
} from '../formatWatchlist';

const MODES: WatchlistNotificationMode[] = ['off', 'instant', 'daily'];

type PropsType = {
    /** null = active but no channel selected: nothing is highlighted. */
    value: WatchlistNotificationMode | null,
    onSelect: (mode: WatchlistNotificationMode) => void,
    disabled?: boolean,
    label: string
};

/** Segmented "Av | Direkt | Dagligen" control. Controlled, has no knowledge of the API. */
export default function NotificationModeControl({
    value,
    onSelect,
    disabled = false,
    label
}: PropsType) {
    return (
        <Box
            role="radiogroup"
            aria-label={label}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            sx={{
                display: 'inline-flex',
                flexShrink: 0,
                alignSelf: { xs: 'flex-start', sm: 'center' },
                borderRadius: 1.5,
                border: `1px solid ${COLOR_OUTLINE}`,
                overflow: 'hidden',
                opacity: disabled ? 0.6 : 1
            }}
        >
            {MODES.map((mode, index) => {
                const selected = value === mode;
                const selectedBg = mode === 'off' ? COLOR_SLATE_LIGHT : COLOR_MINT;
                return (
                    <Tooltip key={mode} title={NOTIFICATION_MODE_HELP_TEXTS[mode]} enterDelay={300} arrow>
                        <Box
                            component="button"
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            disabled={disabled}
                            onClick={() => !selected && onSelect(mode)}
                            sx={{
                                px: 1.75,
                                py: 0.6,
                                border: 'none',
                                borderLeft: index === 0 ? 'none' : `1px solid ${COLOR_OUTLINE}`,
                                bgcolor: selected ? selectedBg : 'transparent',
                                color: COLOR_BLUE,
                                fontFamily: 'inherit',
                                fontSize: '0.8125rem',
                                fontWeight: selected ? 700 : 500,
                                lineHeight: 1.5,
                                cursor: disabled ? 'default' : 'pointer',
                                transition: 'background-color 0.15s ease',
                                '&:hover:not(:disabled)': {
                                    bgcolor: selected ? undefined : alpha(COLOR_OUTLINE, 0.18)
                                },
                                '&:focus-visible': { outline: `2px solid ${COLOR_TEAL}`, outlineOffset: -2 }
                            }}
                        >
                            {NOTIFICATION_MODE_LABELS[mode]}
                        </Box>
                    </Tooltip>
                );
            })}
        </Box>
    );
}
